import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useVersionDateFilter } from 'src/components/star-grid/hooks/useVersionDateFilter';
import {
  initialVersionCalendarMonth,
  moveVersionCalendarMonth,
  VERSION_CALENDAR_MONTH_COUNT,
  versionCalendarKeyboardDate,
  versionCalendarMonth,
  versionCalendarMonthForDate,
  versionCalendarWeekdays
} from 'src/components/star-grid/utils/versionRangeCalendar';

type DateFilter = ReturnType<typeof useVersionDateFilter>;

const useCalendarFocus = (month: string, setMonth: (month: string) => void, initial: string) => {
  const ref = useRef<HTMLDivElement>(null);
  const [focused, setFocused] = useState(initial);
  const pendingFocus = useRef(false);
  useEffect(() => {
    if (!pendingFocus.current) return;
    ref.current?.querySelector<HTMLButtonElement>(`[data-date="${focused}"]`)?.focus();
    pendingFocus.current = false;
  }, [focused, month]);
  const keyDown = (event: KeyboardEvent<HTMLButtonElement>, date: string): void => {
    const next = versionCalendarKeyboardDate(date, event.key, event.shiftKey);
    if (!next) return;
    event.preventDefault();
    pendingFocus.current = true;
    setMonth(versionCalendarMonthForDate(month, next));
    setFocused(next);
  };
  return { ref, focused, setFocused, keyDown };
};

export const useVersionRangeCalendar = (dates: DateFilter) => {
  const [month, setMonth] = useState(() => initialVersionCalendarMonth(dates.draft));
  const focus = useCalendarFocus(month, setMonth, dates.calendarDate || month);
  useEffect(() => setMonth((current) => versionCalendarMonthForDate(current, dates.calendarDate)), [dates.calendarDate]);
  const months = useMemo(
    () =>
      Array.from({ length: VERSION_CALENDAR_MONTH_COUNT }, (_, index) =>
        versionCalendarMonth(moveVersionCalendarMonth(month, index), dates.draft, focus.focused)
      ),
    [month, dates.draft, focus.focused]
  );
  const moveMonth = (offset: number): void => setMonth((current) => moveVersionCalendarMonth(current, offset));
  const choose = (date: string): void => {
    focus.setFocused(date);
    dates.chooseDate(date);
  };
  return { ...focus, months, weekdays: versionCalendarWeekdays(), moveMonth, choose };
};
